function required(key: keyof ImportMetaEnv): string {
  const value = import.meta.env[key];
  if (!value) throw new Error(`Missing required environment variable: ${key}`);
  return value;
}

function optional(key: keyof ImportMetaEnv, fallback = ''): string {
  return (import.meta.env[key] as string | undefined) ?? fallback;
}

/**
 * Force the in-app mock data layer. When `VITE_USE_MOCKS=true`, every admin API hook resolves from
 * realistic sample data instead of hitting the backoffice backend — so the whole UI is demoable with
 * no server. Even when this is off, the API client falls back to mocks if a real request is
 * unreachable (see `@core/api/adminApi`). Local default: ON (so `pnpm dev` works out of the box).
 */
export const useMocks: boolean =
  (import.meta.env.VITE_USE_MOCKS as string | undefined)?.trim().toLowerCase() === 'true';

export const env = {
  apiBaseUrl: required('VITE_API_BASE_URL'),
  // Cognito values are only required when mocks are OFF (a real login is needed to reach the API).
  cognitoAuthority: useMocks ? optional('VITE_COGNITO_AUTHORITY') : required('VITE_COGNITO_AUTHORITY'),
  cognitoClientId:  useMocks ? optional('VITE_COGNITO_CLIENT_ID')  : required('VITE_COGNITO_CLIENT_ID'),
  cognitoHostedUi:  useMocks ? optional('VITE_COGNITO_HOSTED_UI')  : required('VITE_COGNITO_HOSTED_UI'),
  useMocks,
} as const;

/**
 * Build modes that serve REAL customers. The chat-history reader is structurally unavailable in these
 * regardless of configuration — see {@link isChatHistoryEnabled}.
 */
const CUSTOMER_FACING_MODES: readonly string[] = ['prod', 'demo'];

/**
 * Whether the per-strategy AI chat-history reader is available in this build.
 *
 * Two conditions, both required — and note this is the one flag in the app that **fails closed**.
 * Everywhere else (`useCapabilities`) an unknown feature defaults to *on* so the UI is never gated by
 * mistake; here an unset or unparseable flag means **off**, because the cost of wrongly showing a
 * customer's own words to an admin is not symmetric with the cost of wrongly hiding them.
 *
 * 1. `VITE_ENABLE_CHAT_HISTORY=true` — explicit opt-in, set only in `.env.dev` and the local template.
 * 2. The build mode is not customer-facing. This second check is deliberately **not** configurable:
 *    setting the flag in a prod/demo env file cannot switch the feature on, so enabling it there takes
 *    a code change and a review rather than a one-line config edit.
 *
 * Exported as a pure function of its inputs so the gate itself is unit-testable (`import.meta.env` is
 * fixed at module load and can't be varied per test). {@link chatHistoryEnabled} repeats this logic
 * inline so the optimiser can fold it — keep the two in step; the tests pin the behaviour of this one.
 */
export function isChatHistoryEnabled(mode: string, flag: string | undefined): boolean {
  if (CUSTOMER_FACING_MODES.includes(mode)) return false;
  return flag?.trim().toLowerCase() === 'true';
}

/**
 * Read the user's AI chat turns on the deep-dive. **Dev only**: `v2_ai_chat_turns.question` is the
 * customer's own free text. journal-backend's data export (`ExportUserDataHandler`) gives users these
 * turns too, but only as their own screen showed them; this reader shows the stored row, raw reasoning
 * and prompt version included. Showing it against real customer data is a separate decision from
 * building the reader.
 *
 * Written as one inline expression rather than a call to {@link isChatHistoryEnabled} so that Vite can
 * **statically fold it**: `import.meta.env.MODE` is replaced with a string literal at build time, so a
 * `prod`/`demo` build evaluates this to a constant `false` and Rollup drops the chat UI, its hook, its
 * API method and its mock data out of the bundle entirely. A function call would be opaque to the
 * optimiser and would leave the whole feature shipped-but-unreachable. Unreachable is not the same as
 * absent, and for a surface that reads customers' own words the difference is worth the duplication —
 * `isChatHistoryEnabled` keeps the identical logic under test.
 */
export const chatHistoryEnabled: boolean =
  import.meta.env.MODE !== 'prod'
  && import.meta.env.MODE !== 'demo'
  && (import.meta.env.VITE_ENABLE_CHAT_HISTORY as string | undefined)?.trim().toLowerCase() === 'true';

/**
 * Whether the Compliance console (`/compliance*`: the alerts inbox, the risk overview, the ranked users
 * and the per-user risk view) is available in this build. Same two conditions and the same fail-closed
 * reading as {@link isChatHistoryEnabled}: an explicit `VITE_ENABLE_COMPLIANCE=true` AND a build mode
 * that is not customer-facing.
 *
 * The screens are content-blind (ids, dates, rule ids, counts; never a customer's words), so the reason
 * for the gate is a different one from the chat reader's: it is a supervision console for AI features,
 * and the owner's standing rule is that no AI feature reaches production until a prod plan is approved.
 * A `prod` build therefore cannot show it however the env file is set. The pure function is what the
 * tests pin; {@link complianceEnabled} repeats the logic inline so Vite can fold it.
 */
export function isComplianceEnabled(mode: string, flag: string | undefined): boolean {
  if (CUSTOMER_FACING_MODES.includes(mode)) return false;
  return flag?.trim().toLowerCase() === 'true';
}

/**
 * The Compliance console gate, written inline (not a call to {@link isComplianceEnabled}) for the same
 * reason as {@link chatHistoryEnabled}: `import.meta.env.MODE` folds to a literal, so a `prod`/`demo`
 * build evaluates this to `false` and Rollup drops the page chunks, the routes, the nav tab, the P1
 * strip and its poll, the endpoint URLs and the mock data (`mockComplianceApi`) out of the bundle.
 * What survives is inert: the `adminApi.compliance*` method names as stubs that reject, and the query
 * key factory. Verified by grepping a `--mode prod` build (docs/compliance.md).
 */
export const complianceEnabled: boolean =
  import.meta.env.MODE !== 'prod'
  && import.meta.env.MODE !== 'demo'
  && (import.meta.env.VITE_ENABLE_COMPLIANCE as string | undefined)?.trim().toLowerCase() === 'true';
